import { parse } from 'date-fns/parse';
import { isValid } from 'date-fns/isValid';
import { subYears } from 'date-fns/subYears';
import { addHours } from 'date-fns/addHours';
import { isBefore } from 'date-fns/isBefore';
import { removeWhiteSpaces } from './format';

const isEmpty = (value: string): boolean => !value || value === undefined || value === null || value === '';

type Validator = (args: string) => any;

export const composeValidators =
  (...validators: Validator[]) =>
  (value: string): string | undefined =>
    validators.reduce((error, validator) => error || validator(value), undefined);

// two digit year needs to be first in array, otherwise dates will be parsed as 4 digit years. E.g. 010190 => 0090-01-01
const dateFormats = ['ddMMyy', 'dd.MM.yy', 'dd/MM/yy', 'ddMMyyyy', 'dd.MM.yyyy', 'dd/MM/yyyy', 'yyyy-MM-dd'];

const phone = (value: string) => {
  const valueWithoutSpaces = removeWhiteSpaces(value);

  if (!isEmpty(valueWithoutSpaces) && !/^\d{8}$/.test(valueWithoutSpaces)) {
    return 'Mobilnummer må bestå av 8 siffer';
  }
  if (!isEmpty(value) && /.*\s\s.*/.test(value)) {
    return 'Mobilnummeret inneholder for mange mellomrom';
  }
};

const date = (value: string) => {
  const isValidDate = dateFormats.some((format) => isValid(parse(value, format, new Date())));
  if (!isValidDate) return 'Fødselsdato må skrives på formatet ddmmåå';
};

/**
 * Accept date in formats: ddMMyy, dd.MM.yy, dd/MM/yy, ddMMyyyy, dd.MM.yyyy and dd/MM/yyyy
 */
export const getValidDate = (dateString: string) => {
  // TODO: simplify by just using pattern-matching here?
  // TBH the date-fns parsing is just confusing since we need to handle century and timezone offsets to get correct dates
  let validDate = dateFormats.map((format) => parse(dateString, format, new Date())).find((date) => isValid(date));
  if (!validDate) {
    return undefined;
  }

  // Fix 2 digit year parsing so we dont use bDays from the future
  // e.g. for 010130 => 2030-01-01 => 1979-01-01
  if (validDate.getFullYear() > new Date().getFullYear()) {
    validDate.setFullYear(validDate.getFullYear() - 100);
  }

  // Fix TZ offsets after parsing date. E.g. 010190 => parsed as local date then results in UTC date => 1989-12-31T23:00:00.000Z (for summer time 2h diff)
  validDate = addHours(validDate, -validDate.getTimezoneOffset() / 60);
  return validDate;
};

const minAge = (noOfYears: number) => (value: string) => {
  const minDate = subYears(new Date(), noOfYears);
  const validDate = getValidDate(value);

  if (validDate != null && isBefore(minDate, validDate)) {
    return `Må være eldre enn ${noOfYears} år`;
  }
};

const required =
  (message = 'Påkrevd') =>
  (value: string) => {
    if (isEmpty(value)) {
      return message;
    }
  };

const invalidComma = (value: string) => {
  if (value.match(',')) {
    return 'Adressefeltet kan ikke inneholde komma';
  }
};
const zip = (value: string) => {
  if (!/^\d{4}$/.test(value)) {
    return 'Postnummer må bestå av 4 siffer';
  }
};

const email = (value: string): string | undefined => {
  // Autofill on Chrome on iOS adds single space at the end, so we allow this (but trim in checkoutSaga)
  const trimmedValue = value.trim();

  if (
    (!isEmpty(trimmedValue) &&
      !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i.test(
        trimmedValue,
      )) ||
    (!isEmpty(value) && /.*\s\s*./.test(value))
  ) {
    return 'Ugyldig e-post';
  }
};

const text =
  (message = 'Ugyldig') =>
  (value: string) => {
    if (!isEmpty(value) && /([0-9])/.test(value)) return message;
  };

export const validators = {
  required,
  email,
  phone,
  text,
  date,
  minAge,
  invalidComma,
  zip,
};
